// src/hooks/useContacts.js
import { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../context/ToastContext";
import { US_STATES } from "../lib/constants";

export function useContacts(me) {
  const { setToast } = useToast();
  
  // contacts (owner only)
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactDraft, setContactDraft] = useState({
    agency: "",
    email: "",
    portal: "",
    website: "",
    notes: "",
    state: "",
  });
  const [savingContact, setSavingContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // ---------------- CONTACT MANAGEMENT ----------------
  const loadContacts = async () => {
    if (!me?.org_id) return;
    setLoadingContacts(true);
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("*")
        .eq("org_id", me.org_id)
        .order("agency", { ascending: true })
        .limit(2000);
      if (error) throw error;
      setContacts(data || []);
    } catch (e) {
      setToast({ type: "error", msg: `Load contacts failed: ${e.message}` });
    } finally {
      setLoadingContacts(false);
    }
  };

  const saveContact = async () => {
    if (!contactDraft.agency.trim()) {
      setToast({ type: "warn", msg: "Agency is required." });
      return;
    }
    
    // Validate state if provided
    if (contactDraft.state && !US_STATES.some(state => state.value === contactDraft.state)) {
      setToast({ type: "warn", msg: "Please select a valid state from the dropdown." });
      return;
    }
    
    setSavingContact(true);
    try {
      if (editingContact) {
        const { error } = await supabase
          .from("contacts")
          .update(contactDraft)
          .eq("id", editingContact.id)
          .eq("org_id", me?.org_id);
        if (error) throw error;
        setContacts(prev => prev.map(c => c.id === editingContact.id ? { ...c, ...contactDraft } : c));
        setToast({ type: "ok", msg: "Contact updated." });
      } else {
        if (!me?.org_id) throw new Error("No organization context yet.");
        const { error } = await supabase
          .from("contacts")
          .insert({ ...contactDraft, org_id: me?.org_id });
        if (error) throw error;
        await loadContacts();
        setToast({ type: "ok", msg: "Contact created." });
      }
      setContactDraft({ agency: "", email: "", portal: "", website: "", notes: "", state: "" });
      setEditingContact(null);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setSavingContact(false);
    }
  };

  const editContact = (contact) => {
    setEditingContact(contact);
    setContactDraft({
      agency: contact.agency || "",
      email: contact.email || "",
      portal: contact.portal || "",
      website: contact.website || "",
      notes: contact.notes || "",
      state: contact.state || "",
    });
  };

  const deleteContact = async (contact) => {
    const confirmed = window.confirm(`Delete contact: ${contact.agency}?`);
    if (!confirmed) return;
    try {
      const { error } = await supabase
        .from("contacts")
        .delete()
        .eq("id", contact.id)
        .eq("org_id", me?.org_id);
      if (error) throw error;
      setContacts(prev => prev.filter(c => c.id !== contact.id));
      setEditingContact(null);
      setContactDraft({ agency: "", email: "", portal: "", website: "", notes: "", state: "" });
      setToast({ type: "ok", msg: "Contact deleted." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  };

  return {
    contacts,
    setContacts,
    loadingContacts,
    contactDraft,
    setContactDraft,
    savingContact,
    editingContact,
    setEditingContact,
    loadContacts,
    saveContact,
    editContact,
    deleteContact,
  };
}
